import { TimeSeriesDisableRange } from 'utils/timeseries/TimeSeriesDisableRange';
import {
    BucketedDataItem,
    createEmptyBuckets,
    findNextBucket,
} from 'utils/timeseries/bucket';
import { getItemTime } from 'utils/timeseries/timeSeriesUtils';
import { VolumeCurveTimeSeriesData } from 'widgets/volumeCurve/api/VolumeCurveElement.ts';
import { OrderFill } from 'widgets/fillsGraph/fillsGraphData.ts';
import { BucketedData } from 'widgets/fillsGraph/fillsGraphDataBuckets.ts';
import { TimeSeriesChartDataItem } from 'utils/components/TimeSeriesChart/TimeSeriesChartData.ts';
import orderBy from 'lodash/orderBy';

export type VolumeCurveBucketedDataItem = TimeSeriesChartDataItem &
    BucketedDataItem & {
        _i: number;
        tradedVolume: number;
        historicalVolume: number;
        auctionVolume: number;
        cumulativeTradedVolume?: number;
        cumulativeHistoricalVolume: number;
    };

export function bucket(
    bucketSizeInMinutes: number,
    sortedData: VolumeCurveTimeSeriesData[],
    breaks: TimeSeriesDisableRange[]
): VolumeCurveBucketedDataItem[] {
    const bucketedData = createEmptyBuckets<VolumeCurveBucketedDataItem>(
        bucketSizeInMinutes,
        sortedData,
        breaks,
        buildEmptyBucket
    );

    let currentBucket: VolumeCurveBucketedDataItem | undefined =
        bucketedData[0];

    for (const d of sortedData) {
        if (!currentBucket) {
            break;
        }
        const tradedVolume = d.v ? d.v : 0;
        const historicalVolume = d.vp ? d.vp : 0;
        const auctionVolume = d.avp ? d.avp : 0;

        const dataTime = getItemTime(d);

        while (dataTime > currentBucket.endTime) {
            currentBucket = findNextBucket(bucketedData, currentBucket);
            if (!currentBucket) {
                break;
            }
        }

        if (currentBucket) {
            currentBucket.tradedVolume =
                currentBucket.tradedVolume + tradedVolume;
            currentBucket.historicalVolume =
                currentBucket.historicalVolume + historicalVolume;
            currentBucket.auctionVolume =
                currentBucket.auctionVolume + auctionVolume;
        }
    }

    const buckets: VolumeCurveBucketedDataItem[] = bucketedData.map((value) => {
        return {
            time: value.startTime / 60000,
            _i: value.startTime / 60000,
            startTime: value.startTime,
            midTime: value.midTime,
            endTime: value.endTime,
            endTimeDisplay: value.endTimeDisplay,
            tradedVolume: value.tradedVolume,
            historicalVolume: value.historicalVolume,
            auctionVolume: value.auctionVolume,
            cumulativeHistoricalVolume: 0,
            cumulativeTradedVolume: undefined,
        };
    });

    const now = new Date().getTime();
    let cumulativeTradedVolume = 0;
    let cumulativeHistoricalVolume = 0;

    const sortedBuckets = orderBy(buckets, ['midTime'], ['asc']);

    const lastBucketWithTraded =
        sortedBuckets.filter((b) => (b.tradedVolume ?? 0) > 0).reverse()[0]
            ?._i ?? 0;

    for (let x = 0; x < sortedBuckets.length; ++x) {
        const val = sortedBuckets[x];

        if (val._i <= lastBucketWithTraded || val.endTime <= now) {
            val.cumulativeTradedVolume =
                cumulativeTradedVolume + (val.tradedVolume ?? 0);
            cumulativeTradedVolume = val.cumulativeTradedVolume;
        }

        if (val.historicalVolume) {
            val.cumulativeHistoricalVolume =
                cumulativeHistoricalVolume + val.historicalVolume;
            cumulativeHistoricalVolume = val.cumulativeHistoricalVolume;
        }
    }

    return sortedBuckets;
}

export function mergeBucketedData(
    sortedData: OrderFill[],
    bucketedData: BucketedData[]
) {
    for (const bd of bucketedData) {
        const index = sortedData.findIndex((d) => d.time >= bd.startTime);
        if (index >= 0) {
            const item = {
                ...sortedData[index],
                time: bd.startTime,
                bucket: bd,
            };
            if (sortedData[index].time === bd.startTime) {
                sortedData[index] = item;
            } else {
                sortedData.splice(index, 0, item);
            }
        } else {
            sortedData.push({ time: bd.startTime, bucket: bd });
        }
    }
}

function buildEmptyBucket(
    startTime: number,
    _endTime: number
): Partial<VolumeCurveBucketedDataItem> {
    return {
        _i: startTime / 60000,
        time: startTime / 60000,
        historicalVolume: 0,
        tradedVolume: 0,
        auctionVolume: 0,
    };
}
